import { useState } from "react";
import { Container, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { asnSearchAsync } from "../../services/apiHelper";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { SearchBarContainer } from "../../components/Search/components/SearchBar/SearchBarContainer";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import PageContext from "../../components/Common/PageContext";
import React from "react";
import { searchHelperText } from "../../components/Search/constants";
import { isFeatureEnabledForLocation } from "../../util/featureUtil";
import AsnDetailsCard from "@/components/Asn/AsnDetailsCard";

interface AsnSearchProps {
	pageTitle: string;
}
const useStyles = makeStyles((theme) => ({
	title: {
		fontSize: "1.3rem",
		marginBottom: theme.spacing(1),
	},
}));
export default function AsnSearch({ pageTitle }: AsnSearchProps) {
	const [pageContext] = React.useContext(PageContext);
	const {
		userLocation,
		featureConfiguration: { transfers },
	} = pageContext;
	const classes = useStyles();
	const [searchText, setSearchText] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [isShowLoadingDialog, setIsShowLoadingDialog] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isAlertSeverityError, setIsAlertSeverityError] = useState(false);
	const [showErrorAlert, setShowErrorAlert] = useState(false);
	const isTransfersEnabled = isFeatureEnabledForLocation(userLocation, transfers);

	const onSearchAsync = async (value: string) => {
		try {
			setIsShowLoadingDialog(true);
			const response = await asnSearchAsync(value, userLocation);
			setSearchText(value);
			setSearchResults((response).data);
			setIsShowLoadingDialog(false);
		} catch (error) {
			setIsAlertSeverityError(true);
			setSearchResults([]);
			setIsShowLoadingDialog(false);
			setErrorMessage(
				"Could not retrieve search results. Please try again and raise a support ticket if issue persists."
			);
			setShowErrorAlert(true);
		}
	};

	const handleCloseAlertAsync = async (event?: React.SyntheticEvent | MouseEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setShowErrorAlert(false);
		setErrorMessage(null);
		setIsAlertSeverityError(false);
	};

	return (
		<Container disableGutters maxWidth="md">
			{pageTitle && (
				<Typography variant="h5" className={classes.title}>
					{pageTitle}
				</Typography>
			)}
			<SearchBarContainer
				handleSearch={onSearchAsync}
				helperText={isTransfersEnabled ? searchHelperText.asnTransfer : searchHelperText.asnGeneral}
			/>
			{!!searchText && (
				<Typography variant="body2" component="div">
					{searchResults.length} matching result(s) found
				</Typography>
			)}
			{searchResults.map(
				(sr, i) => {
					return (
						<AsnDetailsCard
							key={`asn_search_result_${i}`}
							purchaseOrderNumber={sr.sourceId ? sr.sourceId : sr.purchaseOrderId}
							invoiceNumber={sr.invoiceNumber}
							carrier={sr.carrier}
							dateShipped={sr.dateShipped}
							dateDeliveryExpected={sr.dateDeliveryExpected}
							dateDelivered={sr.dateDelivered}
							consignmentNoteNumber={sr.consignmentNumber}
							consignmentTrackingUrl={sr.consignmentTrackingUrl}
							consignmentStatus={sr.consignmentStatus}
							deliveryNoteNumber={sr.deliveryNoteNumber}
							totalNumberOfUnits={sr.totalNumberOfUnits}
							totalNumberOfPackages={sr.totalNumberOfPackages}
							asnType={sr.type}
							labelId={sr.labelId}
							fromLocationId={sr.fromLocationId}
							fromLocationName={sr.fromLocationName}
							toLocationId={sr.toLocationId}
							toLocationName={sr.toLocationName}
							isLastCard={i === searchResults.length - 1}
						/>
					);
				}
			)}
			<LoadingDialog message="Retrieving results..." isOpen={isShowLoadingDialog} />
			<ModalWithActionButton
				title={isAlertSeverityError ? "Error" : "Warning"}
				alertType={isAlertSeverityError ? "error" : "warning"}
				primaryButtonName="Ok"
				onPrimaryButtonClick={() => handleCloseAlertAsync()}
				isOpen={showErrorAlert}
				message={errorMessage ?? ""}
			/>
		</Container>
	);
}
